/**
 * What this browser keeps in IndexedDB for whoever was signed in, dropped on
 * log out.
 *
 * Log out cleared the session from localStorage but left IndexedDB alone, so
 * the signed-out user's cached conversations and messages (`inmeet-chat`) and
 * any local-recording chunks (`inmeet_recordings`) stayed on the machine for
 * the next person at this browser. The chat cache did notice a different user
 * on the next sign-in, but only once they opened chat — until then the data
 * was there to read.
 *
 * Every database this origin has is deleted, not only the ones named here:
 * anything a library stored for this user goes too. The named ones are
 * covered even where the browser cannot list its databases.
 */
const APP_DATABASES = ['inmeet-chat', 'inmeet_recordings']

/** Longest a log out waits on IndexedDB; a blocked delete still finishes later. */
const CLEAR_TIMEOUT_MS = 3000

function deleteDatabase(name: string): Promise<void> {
  return new Promise((resolve) => {
    try {
      const req = indexedDB.deleteDatabase(name)
      req.onsuccess = () => resolve()
      req.onerror = () => resolve()
      // Another tab still holds it open. The delete runs once that tab lets
      // go; log out does not wait for it (see the timeout below).
    } catch {
      resolve()
    }
  })
}

export async function clearIndexedDb(): Promise<void> {
  if (typeof indexedDB === 'undefined') return
  const names = new Set(APP_DATABASES)
  try {
    const listed = await indexedDB.databases?.()
    for (const db of listed ?? []) if (db.name) names.add(db.name)
  } catch {
    /* listing unsupported — the named databases are still cleared */
  }
  await Promise.race([
    Promise.all([...names].map(deleteDatabase)),
    new Promise<void>((resolve) => setTimeout(resolve, CLEAR_TIMEOUT_MS)),
  ])
}
